import { Box, Button, Dialog, IconButton, List, ListItem, Stack, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as SaveTitleIcon }  from '../../../assets/save-outline-rounded_title.svg';
import LoaderComponent from 'components/Loader';
import SearchFilter from 'components/Dropdown/SearchFilter';
import CustomTextField from 'components/Inputs/CustomTextField';
import { deleteSavedQuery, editQueryDetails, fetchSavedQueryList, saveSearchedQuery } from 'service/service';
import { formatQuery } from 'react-querybuilder';
import { ReactComponent as EditIcon }  from '../../../assets/material-symbols_edit-rounded_blue.svg';
import { ReactComponent as DeleteIcon }  from '../../../assets/mdi_delete_blue.svg';
import { queryPrefOptions } from './QuerySearchHistory';
import ProjectConfirmationDialog from './ProjectConfirmationDialog';
import { getDateRangeLabel, getSectorLabel, timestampCal } from 'utils/common-util';
import { queryPreferenceOptions } from './ResourceCostSummary';
import eventEmitter from 'SnackBarEventEmitter';
interface SavedQueriesModalProps {
    setIsSavedQueriesModalOpen: any;
	query: any;
	setQuery: any;
	setIsReapplied: any;
	queryPreference: any;
	setQueryPreference : any;
	selectedCheckbox: any;
	setSelectedCheckbox: any;
	selGroupPaths: any;
	setSelGroupPaths: any;
	isProjectDetails: any;
	selectedValues: any;
	setSelectedValues: any;
	setSavedQuery: any;
	onSavedQueriesChange?: any;
	isCost?: boolean;
	customMonth?: string;
	filterMonth?: string;
	setCustomMonth?: (value: string) => void;
	setFilterMonth?: (value: string) => void;
	sectorName?: string;
	setSectorName?: (value: string) => void;
}

const SavedQueriesModal: React.FC<SavedQueriesModalProps> = ({ 
	setIsSavedQueriesModalOpen, 
	query, 
	setQuery, 
	setIsReapplied, 
	queryPreference, 
	setQueryPreference, 
	selectedCheckbox,
	setSelectedCheckbox, 
	selGroupPaths,
	setSelGroupPaths,
	isProjectDetails,
	selectedValues,
	setSelectedValues,
	setSavedQuery,
	onSavedQueriesChange,
	isCost = false,
	customMonth,
	filterMonth,
	setCustomMonth,
	setFilterMonth,
	sectorName,
	setSectorName }: any) => {
	const theme= useTheme();
	const colors = theme.palette;
	const [savedQueries, setSavedQueries] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [queryName, setQueryName] = useState<string>('');
	const [saveBtnLoader, setSaveBtnLoader] = useState(false);
	const [formattedQuery, setFormattedQuery] = useState<any>('');
	const [editRecord, setEditRecord] = useState<any>('');
	const [isEdit, setIsEdit] = useState(false);
	const [openConfirmationDialog, setOpenConfirmationDialog] = useState<any>({ isOpen: false, message: '', isUnlink: false, isCancel: false, isQuery: true })

	function queryParam(sql: any, param: any) {
		let paramIndex = 0;
		return sql.replace(/\?/g, () => {
		  const params = param[paramIndex++];
		  return typeof params === 'string' ? `'${params}'` : params;
		});
	}

	function getQueryPrefLabel(queryPreference: string) {
		const queryPref = isCost ? queryPreferenceOptions : queryPrefOptions;
		const match = queryPref.find(option => option.value === queryPreference);
		return match ? match.label : '';
	};

	const handleReapply = (selItem: any) => {
		setQuery(selItem.queryJson);
		setQueryPreference(selItem.queryPreference ?? 'Resource_flat');
		setIsReapplied(true);
		setSelectedCheckbox(selItem.selectedPaths); 
    	setSelGroupPaths(selItem.groupPaths); 
		let selectedColumns = selItem.selectedColumns;
		if (typeof selectedColumns === 'string') {
			selectedColumns = selectedColumns.split(',').map(col => col.trim());
		}
		const baseColumns = Array.isArray(selectedColumns) ? selectedColumns : [];
		const updatedSelectedColumns = [
			"Actions",
			"HeaderCheckboxMapping",
			...baseColumns.filter(col => col !== "Actions" && col !== "HeaderCheckboxMapping")
		];
		const storageKey = isProjectDetails ? 'resource-details_user_col_pref' : 'resource-list_user_col_pref';
		localStorage.setItem(storageKey, JSON.stringify(updatedSelectedColumns));
		setSelectedValues({
			program: selItem.program && selItem.program !== '0' ? selItem.program : '',
			project: selItem.project && selItem.project !== '0' ? selItem.project : ''
		});
		
		setCustomMonth?.(selItem.customMonth || '');
		setFilterMonth?.(selItem.filterMonth || '');
		setSectorName?.(selItem.sectorName || 'all');
		
		if(!!isProjectDetails) setSavedQuery(selItem);
		setIsSavedQueriesModalOpen(false); 
	};

	const handleCancelClick = () => {
		setQueryName('');
		if(!!isEdit) {
			setEditRecord('');
			setIsEdit(false);
		}
	};

	const handleSaveQuery = async (event: any) => {
		try {
			if (saveBtnLoader) return;
			setSaveBtnLoader(true);
			event.preventDefault();
			let result;
			let formPayload;
			if(!!isEdit) {
				formPayload = {
					queryId: editRecord.queryId,
					queryName: queryName,
					isCost: isCost,
				};
				result = await editQueryDetails(formPayload);
			}
			else {
				const storageKey = isProjectDetails ? 'resource-details_user_col_pref' : 'resource-list_user_col_pref';
				const fieldsStr = localStorage.getItem(storageKey);
				const fields = fieldsStr ? JSON.parse(fieldsStr) : [];
				formPayload = {
					queryName: queryName,
					query: formattedQuery,
					queryJson: query,
					selectedColumns: fields,
					queryPreference: queryPreference,
					selectedPaths: selectedCheckbox,
					groupPaths: selGroupPaths,
					program : '',
					project : '',
					isSaveQuery: true,
					...(isCost && {
						isCost,
						dateRange: filterMonth || customMonth,
						sector: sectorName || 'all'
					})
				};
				result = await saveSearchedQuery(formPayload);
			}
			if(result.success) {
				if(!!isEdit) {
					setEditRecord('');
					setIsEdit(false);
				}
				setQueryName('');
				getSavedQueryList();
				onSavedQueriesChange?.();
				setSaveBtnLoader(false);
			}
		} catch (err) {
			console.error(err);
			if (typeof err === 'object' && err !== null && 'response' in err) {
				const error = err as { response: { status: number } };
				if (error.response.status === 503)
					eventEmitter.emit('showSnackbar', 'Service temporarily unavailable. Please try again later.', 'info');
				else if (error.response.status === 504) eventEmitter.emit('showSnackbar', 'Gateway Timeout', 'info');
			}
		}
	};

	const handleConfirmationDialogClose = (isCancel: any = true) => {
		setOpenConfirmationDialog({
			...openConfirmationDialog,
			isOpen: false,
			isCancel: isCancel
		});
	};

	const handleDeleteClick = async (delItem: any) => {
		setOpenConfirmationDialog({
			...openConfirmationDialog,
			isOpen: true,
			data: delItem,
			isCost: isCost,
		});
	}

	const handleDelete = async (delItem: any) => {
		const data = delItem.data;
		try {
			setLoading(true);
			if (saveBtnLoader) return;
			setSaveBtnLoader(true);
			let result = await deleteSavedQuery(data.queryId, data.program, data.project, '', isCost);
			if(result.success) {
				handleConfirmationDialogClose(false);
				getSavedQueryList();
				onSavedQueriesChange?.();
				setSaveBtnLoader(false);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleRename = async (item: any) => {
		try {
			setEditRecord('');
			setIsEdit(false);
			if(!!item) {
				setEditRecord(item);
				setIsEdit(true);
				setQueryName(item.queryName);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleSearchData = async (searchText: string) => {
		try {
			await getSavedQueryList(searchText);
		} catch (error) {
			console.error(error);
		}
	};

	const handleOnChange = (event: any) => {
		const value = (event && event?.target?.value) ?? '';
		setQueryName(value.trim() === '' ? '' : value);
	};

	async function getSavedQueryList(searchText?: any) {
		try {
			setLoading(true);
			let res = await fetchSavedQueryList(searchText, '', '', '', isCost);
			if (res.success) {
				const formattedData = res.data.map((item: any) => {
					const baseItem = {
						...item,
						queryJson:
							typeof item.queryJson === 'string'
								? JSON.parse(item.queryJson)
								: item.queryJson,
						selectedPaths:
							typeof item.selectedPaths === 'string'
								? JSON.parse(item.selectedPaths)
								: item.selectedPaths,
						groupPaths:
							typeof item.groupPaths === 'string'
								? JSON.parse(item.groupPaths)
								: item.groupPaths,
						param:
							typeof item.param === 'string'
								? item.param.split(',')
								: item.param,
					};
					
					if (isCost) {
						let filterMonthVal = '';
						let customMonthVal = '';

						if (item.dateRange) {
							if (item.dateRange.includes('-')) {
								customMonthVal = item.dateRange;
							} else {
								filterMonthVal = item.dateRange;
							}
						}
						return {
							...baseItem,
							filterMonth: filterMonthVal,
							customMonth: customMonthVal,
							sectorName: item.sector || 'all'
						};
					}
					return baseItem;
				});
				setSavedQueries(formattedData);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}

	const setQueryValue = () => {
		const filteredRules = query.rules.map((rule: { field: string; operator: string; value: string; id: string }) => {
			if (rule.field === "") {
				return { field: "1", operator: "=", value: "1", id: rule.id };
			}
			return rule;
		});
		const values = formatQuery({ ...query, rules: filteredRules }, 'parameterized');
		setFormattedQuery(values);
	}

	//fetching data
	useEffect(() => {
		setIsReapplied(false);
		setQueryValue();
		getSavedQueryList();
	}, []);

	return (
		<Stack direction="column" p={4} position='relative' sx={{ backgroundColor: `${colors.overlay.background} !important`, color: `${colors.overlay.title} !important` }}>
			<Stack minWidth='250px'
				direction="row"
				spacing={1}
				pb={2}
				marginBottom={1}
				justifyContent="space-between"
				borderBottom={`0.5px solid ${colors.overlay.border} !important`}
			>
				<Stack direction="row" alignItems="flex-start" spacing={1}
					sx={{
						position: 'absolute',
						top: 15,
						'& svg path': {
							fill: `${colors.overlay.IconColor} !important`
						}
					}}
				>
					<SaveTitleIcon className='close-icon' style={{ width: '24px', height: '24px' }}/>
					<Typography fontSize="16px" sx={{ color: `${colors.overlay.title} !important`, fontWeight: 500 }}>All Saved Queries</Typography>
				</Stack>
				<IconButton 
					onClick={() => setIsSavedQueriesModalOpen(false)} // Close drawer
					sx={{
						position: 'absolute',
						top: 8,
						right: 6,
						color: `${colors.overlay.title} !important`,
						'& svg': {
							color: `${colors.overlay.IconColor} !important`,
							fill: `${colors.overlay.IconColor} !important`
						},
						'& svg path': {
							fill: `${colors.overlay.IconColor} !important`
						}
					}}
				>
					<CloseIcon sx={{ color: `${colors.overlay.IconColor} !important`, fill: `${colors.overlay.IconColor} !important` }}/>
				</IconButton>
			</Stack>
			{isEdit && (
			<Stack direction="column"
				spacing={1.5}
				pb={2}
				marginBottom={1}
				justifyContent="space-between"
				borderBottom={`0.5px solid ${colors.overlay.border} !important`}>
				<Typography sx={{ color: `${colors.overlay.title} !important` }}>Rename Selected Query</Typography>
				<CustomTextField 
					label="Query Name *"
					id="queryName"
					name="queryName"
					variant="outlined"
					autoComplete="off"
					value={queryName}
					onChange={handleOnChange}
					fullWidth
					InputLabelProps={{
						sx: {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							padding: '0 6px',
							zIndex: 1,
							'&.Mui-focused': {
								color: `${colors.inputNew.inputPlaceholder} !important`,
							},
							'&.MuiInputLabel-shrink': {
								color: `${colors.inputNew.inputPlaceholder} !important`,
							}
						}
					}}
					InputProps={{
						sx: {
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.inputNew.inputBorder} !important`,
							},
							'&:hover .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.inputNew.inputBorderHover} !important`,
							},
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.inputNew.inputBorderHover} !important`,
							},
							'& .MuiInputBase-input': {
								color: `${colors.inputNew.inputText} !important`,
							}
						}
					}}
					sx={{
						'& .MuiInputLabel-root': {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							zIndex: 1
						},
						'& .MuiInputLabel-root.Mui-focused': {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							padding: '0 6px'
						},
						'& .MuiInputLabel-root.MuiInputLabel-shrink': {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							padding: '0 6px'
						},
						'& .MuiOutlinedInput-root': {
							border: 'none'
						}
					}}
				/>
				<Stack direction="row" spacing={1.5}>
					<Button sx={{border: `1px solid ${colors.buttons.primary.borderedBorder} !important`, color: `${colors.buttons.primary.color} !important`, flex: 1}} 
						variant="pepwiseSecondaryOutline2" onClick={handleCancelClick}>
						CANCEL
					</Button>
					<Button disabled={!queryName} variant="pepwiseSecondary" onClick={handleSaveQuery} sx={{ flex: 1 }}>
						{saveBtnLoader ? 'SAVING...' : 'SAVE'}
					</Button>
				</Stack>
			</Stack>
			)}
			<Stack direction="column"
				spacing={1}
				justifyContent="space-between">
				<Typography sx={{ color: `${colors.overlay.title} !important`, fontWeight: 500 }}>Saved Queries</Typography>
				<SearchFilter
					handleSearchData={handleSearchData}
					sx={{width: '100%'}}
					hasClearBtn={true}
					backgroundColor={colors.filterDropdown.searchBackground}
					textColor={colors.inputNew.inputText}
					placeholderColor={colors.inputNew.inputPlaceholder}
					searchIconColor={colors.inputNew.inputPlaceholder}
					clearIconColor={colors.inputNew.inputPlaceholder}
					borderColor={colors.inputNew.inputPlaceholder}
				/>
				{loading ? (
					<LoaderComponent />
				) : ( 
					<Box sx={{maxHeight: 'calc(100vh - 165px)', overflowY: 'auto'}}>
						<List>
						{savedQueries.length === 0 && (
							<Typography mt={2} ml={2} sx={{ color: `${colors.overlay.title} !important` }}>No saved queries found.</Typography>
						)}
						{savedQueries.map((item, index) => (
							<ListItem className="search-item"
							key={item.id}
							alignItems="flex-start"
							sx={{ flexDirection: 'column', alignItems: 'flex-start', width: '100%',
								padding: savedQueries.length - 1 === index ? '0px 25px 0px 0px' : '0px 25px 8px 0px' }}
							>
								<Stack
									sx={{
										backgroundColor: `${colors.queryBuilder.cardBackground} !important`,
										color: `${colors.queryBuilder.title} !important`,
										padding: 1.5,
										borderRadius: 1,
										mb: 0.5,
										display: 'flex',
										alignItems: 'flex-start',
										width: '100%',
									}}>
									<Stack direction="column" width={'100%'}
										pb={0.5}
										marginBottom={1}
										justifyContent="space-between"
										>
										<Typography sx={{
											fontWeight: 500,
											color: `${colors.queryBuilder.title} !important`,
											fontSize: '20px'
										}}>
											{item.queryName}
										</Typography>
									</Stack>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="flex-start"
										sx={{ width: '100%', gap: 1 }}
										>
										<Typography
											sx={{
											flex: 1,
											minWidth: 0,
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											pr: 1, 
											color: `${colors.queryBuilder.text} !important`,
											}}
										>
											<Box component="div" sx={{ display: 'block', wordBreak: 'break-word', pl: '16px', textIndent: '-16px' }}>
												<Box component="span" sx={{ color: `${colors.queryBuilder.primary} !important`, fontWeight: 500 }}>
													Query Preference:{' '}
												</Box>
												<Box component="span">{getQueryPrefLabel(item.queryPreference)}</Box>
											</Box>
											{isCost && (
												<>
													<Box component="div" sx={{ mt: 0.5 }}>
														<Box component="span" sx={{ color: `${colors.queryBuilder.primary} !important`, fontWeight: 500 }}>
															Sector:{' '}
														</Box>
														<Box component="span">
															{getSectorLabel(item.sector)}
														</Box>
													</Box>
													<Box component="div" sx={{ mt: 0.5, pl: '16px', textIndent: '-16px' }}>
														<Box component="span" sx={{ color: `${colors.queryBuilder.primary} !important`, fontWeight: 500 }}>
															Date Range:{' '}
														</Box>
														<Box component="span">
															{getDateRangeLabel(item.filterMonth, item.customMonth)}
														</Box>
													</Box>
												</>
											)}
											<Box component="div" sx={{ color: `${colors.queryBuilder.primary} !important`, fontWeight: 500, marginTop: '2px' }}>
												Where{' '}
											</Box>
											<Box component="div" sx={{ display: 'block', ml: 2 }}>
												{queryParam(item.query, item.param)}
											</Box>
										</Typography>
										<Typography sx={{ color: `${colors.queryBuilder.subText} !important`, whiteSpace: 'nowrap', textAlign: 'right', flexShrink: 0, minWidth: 'max-content' }}>
											{timestampCal(new Date(item.updatedAt))}
										</Typography>
									</Stack>
									<Stack  direction="row" justifyContent="flex-end" sx={{ width: '100%', mt: 1.5 }}>
										<Box display="flex" gap={1}>
											<Button sx={{border: `1px solid ${colors.queryBuilder.primary}`, color: `${colors.queryBuilder.primary} !important`, fontWeight: '700',
												textTransform: 'uppercase', minWidth: 80, py: 0.5 }} variant="pepwiseSecondaryOutline2"
												onClick={() => handleDeleteClick(item)} startIcon={<DeleteIcon />} 
												disabled={!!isEdit && editRecord.queryId === item.queryId}>
												Delete
											</Button>
											<Button sx={{border: `1px solid ${colors.queryBuilder.primary}`, color: `${colors.queryBuilder.primary} !important`, fontWeight: '700',
												textTransform: 'uppercase', minWidth: 80, py: 0.5 }} variant="pepwiseSecondaryOutline2"
												onClick={() => handleRename(item)} startIcon={<EditIcon />} 
												disabled={(!!isEdit && editRecord.queryId === item.queryId) 
												|| (item.program && item.project && item.program !== '0' && item.project !== '0')}>
												Rename
											</Button>
											<Button variant="pepwiseSecondary" sx={{ minWidth: 90, py: 0.5  }} onClick={() => handleReapply(item)}
												disabled={!!isEdit && editRecord.queryId === item.queryId}>
												Apply
											</Button>
										</Box>
									</Stack>
								</Stack>
							</ListItem>
						))}
						</List>
					</Box>
				)}
			</Stack>
			{!loading && savedQueries && savedQueries.length > 0 && (
				<Typography textAlign="left" sx={{ color:  `${colors.queryBuilder.subText} !important`, margin: '10px 0px 0px 0px', lineHeight: 1 }}>
					{`Showing ${savedQueries.length} Results`}
				</Typography>
			)}
			<Dialog open={openConfirmationDialog.isOpen} onClose={handleConfirmationDialogClose} fullWidth={true} maxWidth="md">
				<ProjectConfirmationDialog
					handleDialogClose={handleConfirmationDialogClose}
					projectData={openConfirmationDialog}
					handleSave={handleDelete} 
					/>
			</Dialog>
		</Stack>
	);
};

export default SavedQueriesModal;